// api/horama.ts - Horama Integration API
import { fetchAPI } from "./utils";

// Types
export interface HoramaConfigStatus {
    configured: boolean;
    instanceUrl?: string;
    zooSlug?: string;
    speciesCount?: number;
    lastConnected?: string;
}

export interface HoramaSpecies {
    id: string;
    labels: Record<string, string>;
}

export interface HoramaSpeciesResponse {
    species: HoramaSpecies[];
    zooSlug: string;
    count: number;
    availableLanguages?: string[];
    defaultLanguage?: string | null;
    configured?: boolean;
}

export interface UpdateHoramaConfigParams {
    token: string;
    companyId: string;
    instanceUrl: string;
    apiKey: string;
    testConnection?: boolean;
}

export interface RemoveHoramaConfigParams {
    token: string;
    companyId: string;
}

export interface GetHoramaSpeciesParams {
    token: string;
}

export interface GetHoramaConfigStatusParams {
    token: string;
    companyId: string;
}

/**
 * Normalize Horama species payload (legacy string[] or { id, labels }[]).
 */
export function normalizeHoramaSpeciesList(rawSpecies: unknown): HoramaSpecies[] {
    if (!Array.isArray(rawSpecies)) return [];

    return rawSpecies
        .map((item): HoramaSpecies | null => {
            if (typeof item === "string") {
                const id = item.trim();
                return id ? { id, labels: {} } : null;
            }
            if (item && typeof item === "object" && "id" in item) {
                const id = typeof (item as { id: unknown }).id === "string" ? (item as { id: string }).id.trim() : "";
                if (!id) return null;
                const rawLabels = (item as { labels?: unknown }).labels;
                const labels =
                    rawLabels && typeof rawLabels === "object" && !Array.isArray(rawLabels)
                        ? Object.fromEntries(
                              Object.entries(rawLabels as Record<string, unknown>).filter(
                                  (entry): entry is [string, string] =>
                                      typeof entry[1] === "string" && entry[1].trim().length > 0,
                              ),
                          )
                        : {};
                return { id, labels };
            }
            return null;
        })
        .filter((item): item is HoramaSpecies => item !== null);
}

/**
 * Pick a display label for a species, with fallbacks.
 */
export function getHoramaSpeciesLabel(
    species: HoramaSpecies,
    preferredLanguage?: string | null,
    defaultLanguage?: string | null,
): string {
    const labels = species.labels || {};
    const candidates = [preferredLanguage?.toLowerCase(), defaultLanguage?.toLowerCase(), "fr", "en"].filter(
        Boolean,
    ) as string[];

    for (const lang of candidates) {
        if (labels[lang]) return labels[lang];
    }

    const firstLabel = Object.values(labels)[0];
    if (firstLabel) return firstLabel;

    // Legacy fallback when Horama still returns bare IDs
    return species.id.charAt(0).toUpperCase() + species.id.slice(1).replace(/_/g, " ");
}

// API Functions

/**
 * Get Horama configuration status for the selected company
 * @param companyId - ID of the company being managed in the admin panel
 */
export const getHoramaConfigStatus = async ({
    token,
    companyId,
}: GetHoramaConfigStatusParams): Promise<HoramaConfigStatus> => {
    return fetchAPI<HoramaConfigStatus>(
        `/get-horama-config-status?companyId=${encodeURIComponent(companyId)}`,
        "GET",
        undefined,
        token,
    );
};

/**
 * Update or set Horama configuration for the selected company
 * @param companyId - ID of the company being managed in the admin panel
 * @param instanceUrl - Horama instance URL (e.g., https://thoiry.horama.ai)
 * @param apiKey - Horama API key (starts with prism_)
 * @param testConnection - Whether to test connection before saving (default: true)
 */
export const updateHoramaConfig = async ({
    token,
    companyId,
    instanceUrl,
    apiKey,
    testConnection = true,
}: UpdateHoramaConfigParams): Promise<{ message: string; horamaConfig: HoramaConfigStatus }> => {
    return fetchAPI<{ message: string; horamaConfig: HoramaConfigStatus }>(
        "/update-horama-config",
        "POST",
        { companyId, instanceUrl, apiKey, testConnection },
        token,
    );
};

/**
 * Remove Horama configuration from the selected company
 * @param companyId - ID of the company being managed in the admin panel
 */
export const removeHoramaConfig = async ({
    token,
    companyId,
}: RemoveHoramaConfigParams): Promise<{ message: string }> => {
    return fetchAPI<{ message: string }>("/remove-horama-config", "POST", { companyId }, token);
};

/**
 * Get available species from Horama for the current company
 * Used when creating/editing scannable objects with HORAMA provider
 */
export const getHoramaSpecies = async ({ token }: GetHoramaSpeciesParams): Promise<HoramaSpeciesResponse> => {
    const response = await fetchAPI<
        HoramaSpeciesResponse & {
            available_languages?: string[];
            default_language?: string;
            zoo_slug?: string;
        }
    >("/get-horama-species", "GET", undefined, token);

    return {
        configured: response.configured,
        zooSlug: response.zooSlug ?? response.zoo_slug ?? "",
        count: response.count ?? 0,
        availableLanguages: response.availableLanguages ?? response.available_languages,
        defaultLanguage: response.defaultLanguage ?? response.default_language ?? null,
        species: normalizeHoramaSpeciesList(response.species),
    };
};
